import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import UserVerificationModal from '../components/UserVerificationModal';
import { 
  getAllUsers, 
  updateUserAccount, 
  updateTransaction,
  createNewUserWithAccounts,
  subscribeToUserUpdates,
  updateUserStatus,
  rejectUserRegistration,
  requestAdditionalInfo,
  addAdminTransaction,
  findUserByEmail,
  deleteAllUserTransactions,
  deleteSpecificTransaction,
  subscribeToTransactions
} from '../utils/firestore';
import { VALID_OTPS, USED_OTPS, addOTP, removeOTP, resetUsedOTPs } from '../utils/otpValidation';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';

const otpStyles = {
  section: {
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  addSection: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem'
  },
  input: {
    padding: '0.5rem',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    width: '200px'
  },
  button: {
    padding: '0.5rem 1rem',
    backgroundColor: '#0d6efd',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  removeButton: {
    padding: '0.25rem 0.5rem',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem'
  },
  otpItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem',
    backgroundColor: 'white',
    borderRadius: '4px',
    marginBottom: '0.5rem'
  },
  otpLists: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2rem'
  },
  otpListContainer: {
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  }
};

// ImgBB API key
const IMGBB_API_KEY = '9aa0ca74fa1635cd69532671265a980f';

export default function AdminDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingAccount, setEditingAccount] = useState(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [newOtp, setNewOtp] = useState('');
  const [validOtps, setValidOtps] = useState([]);
  const [usedOtps, setUsedOtps] = useState([]);
  const [profileImage, setProfileImage] = useState('');
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    role: 'user',
    isJointAccount: false,
    jointAccountHolder: {
      name: '',
      email: '',
      phone: ''
    },
    accounts: [
      { type: 'Checking', balance: 0, available: 0 },
      { type: 'Savings', balance: 0, available: 0 }
    ]
  });
  const [newTransactions, setNewTransactions] = useState([{
    accountType: '',
    type: 'debit',
    amount: '',
    description: '',
    category: '',
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().slice(0, 5),
    status: 'Completed',
    updateBalance: true
  }]);
  const [emailToDelete, setEmailToDelete] = useState('');
  const [deleteStatus, setDeleteStatus] = useState('');
  const [userTransactions, setUserTransactions] = useState([]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const usersData = await getAllUsers();
        setUsers(usersData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load users');
        setLoading(false);
      }
    };

    loadInitialData();

    const unsubscribe = subscribeToUserUpdates((updatedUsers) => {
      setUsers(updatedUsers);
      if (selectedUser) {
        const updatedUser = updatedUsers.find(u => u.uid === selectedUser.uid);
        if (updatedUser) setSelectedUser(updatedUser);
      }
    });

    return () => unsubscribe();
  }, [selectedUser?.uid]);

  useEffect(() => {
    if (selectedUser?.uid) {
      const unsubscribe = subscribeToTransactions(selectedUser.uid, (transactions) => {
        setUserTransactions(transactions);
      });
      return () => unsubscribe();
    }
  }, [selectedUser]);

  useEffect(() => {
    // Update OTP lists whenever they change
    setValidOtps(Array.from(VALID_OTPS));
    setUsedOtps(Array.from(USED_OTPS));
  }, [showOtpSection]);

  useEffect(() => {
    if (selectedUser?.uid) {
      const savedImage = localStorage.getItem(`profile_image_${selectedUser.uid}`);
      if (savedImage) {
        setProfileImage(savedImage);
      } else {
        setProfileImage('');
      }
    }
  }, [selectedUser]);

  const handleEditAccount = (account) => {
    setEditingAccount({
      id: account.id,
      type: account.type,
      available: parseFloat(account.available || 0),
      balance: parseFloat(account.balance || 0),
      accountNumber: account.accountNumber,
      routingNumber: account.routingNumber
    });
  };

  const handleAccountUpdate = async (accountId, updates) => {
    try {
      setError('');
      await updateUserAccount(selectedUser.uid, accountId, {
        available: parseFloat(updates.available),
        balance: parseFloat(updates.balance),
        accountNumber: updates.accountNumber,
        routingNumber: updates.routingNumber
      });
      setEditingAccount(null);
    } catch (err) {
      setError('Failed to update account');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await createNewUserWithAccounts(newUserData);
      setIsCreatingUser(false);
      setNewUserData({
        email: '',
        password: '',
        name: '',
        phone: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: ''
        },
        role: 'user',
        isJointAccount: false,
        jointAccountHolder: {
          name: '',
          email: '',
          phone: ''
        },
        accounts: [
          { type: 'Checking', balance: 0, available: 0 },
          { type: 'Savings', balance: 0, available: 0 }
        ]
      });
    } catch (err) {
      setError('Failed to create user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserStatusUpdate = async (userId, newStatus) => {
    try {
      setError('');
      await updateUserStatus(userId, newStatus);
      setShowVerificationModal(false);
    } catch (err) {
      setError('Failed to update user status');
    }
  };

  const handleRejectRegistration = async (userId, reason) => {
    try {
      setError('');
      await rejectUserRegistration(userId, reason);
      setShowVerificationModal(false);
    } catch (err) {
      setError('Failed to reject registration');
    }
  };

  const handleRequestInfo = async (userId, requestDetails) => {
    try {
      setError('');
      await requestAdditionalInfo(userId, requestDetails);
      setShowVerificationModal(false);
    } catch (err) {
      setError('Failed to send information request');
    }
  };

  const handleAddTransactionRow = () => {
    setNewTransactions([...newTransactions, {
      accountType: '',
      type: 'debit',
      amount: '',
      description: '',
      category: '',
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().slice(0, 5),
      status: 'Completed',
      updateBalance: true
    }]);
  };

  const handleRemoveTransactionRow = (index) => {
    const updatedTransactions = newTransactions.filter((_, i) => i !== index);
    setNewTransactions(updatedTransactions);
  };

  const handleTransactionChange = (index, field, value) => {
    const updatedTransactions = [...newTransactions];
    updatedTransactions[index] = {
      ...updatedTransactions[index],
      [field]: value
    };
    setNewTransactions(updatedTransactions);
  };

  const handleBulkTransactionSubmit = async (e) => {
    e.preventDefault();
      setError('');
    setLoading(true);
      
    try {
      // Process each transaction
      for (const transaction of newTransactions) {
        const dateTime = new Date(`${transaction.date}T${transaction.time}`);
      await addAdminTransaction(selectedUser.uid, {
          ...transaction,
        date: dateTime
      });
      }
      
      // Instead of resetting to a single empty transaction, just clear the values of existing transactions
      setNewTransactions(newTransactions.map(() => ({
        accountType: '',
        type: 'debit',
        amount: '',
        description: '',
        category: '',
        date: new Date().toISOString().slice(0, 10),
        time: new Date().toTimeString().slice(0, 5),
        status: 'Completed',
        updateBalance: true
      })));

      // Show success message
      setError('Transactions added successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (err) {
      setError('Failed to add transactions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const text = await file.text();
        const rows = text.split('\n');
        const headers = rows[0].split(',');
        
        const transactions = rows.slice(1)
          .filter(row => row.trim()) // Skip empty rows
          .map(row => {
            const values = row.split(',');
            const transaction = {
              accountType: values[0] || '',
              type: values[1] || 'debit',
              amount: values[2] || '',
              description: values[3] || '',
              category: values[4] || '',
              date: values[5] || new Date().toISOString().slice(0, 10),
              time: values[6] || new Date().toTimeString().slice(0, 5),
              status: values[7] || 'Completed',
              updateBalance: true
            };
            return transaction;
          });
        
        setNewTransactions(transactions);
    } catch (err) {
        setError('Failed to parse CSV file: ' + err.message);
      }
    }
  };

  const downloadTemplate = () => {
    const headers = ['Account Type,Transaction Type,Amount,Description,Category,Date,Time,Status'];
    const example = ['Checking,debit,100.00,Sample Transaction,Shopping,2024-03-14,14:30,Completed'];
    const csv = [headers, example].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'transaction_template.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDeleteUserTransactions = async () => {
    try {
      setDeleteStatus('Finding user...');
      const user = await findUserByEmail(emailToDelete);
      
      if (!user) {
        setDeleteStatus('User not found');
        return;
      }

      setDeleteStatus('Deleting transactions...');
      const deletedCount = await deleteAllUserTransactions(user.id);
      setDeleteStatus(`Successfully deleted ${deletedCount} transactions`);
      setEmailToDelete('');
    } catch (error) {
      console.error('Error deleting transactions:', error);
      setDeleteStatus('Error deleting transactions');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      setDeleteStatus('Deleting transaction...');
      await deleteSpecificTransaction(selectedUser.uid, transactionId);
      setDeleteStatus('Transaction deleted successfully');
      setTimeout(() => setDeleteStatus(''), 3000);
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setDeleteStatus('Error deleting transaction');
    }
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'all') return true;
    return user.status === filter;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format account number in 3-3-4 groups for display
  const formatAccountNumber = (number) => {
    if (!number) return '';
    const cleaned = number.replace(/\s/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]}`;
    }
    return number;
  };

  // Validate account number format
  const validateAccountNumber = (number) => {
    const cleaned = number.replace(/\s/g, '');
    return /^\d{10}$/.test(cleaned) && cleaned[0] !== '0';
  };

  const handleAddOtp = () => {
    if (addOTP(newOtp)) {
      setNewOtp('');
      setValidOtps(Array.from(VALID_OTPS));
    } else {
      alert('Invalid OTP format. Please enter a 6-digit number.');
    }
  };

  const handleRemoveOtp = (otp) => {
    if (removeOTP(otp)) {
      setValidOtps(Array.from(VALID_OTPS));
    }
  };

  const handleResetUsedOtps = () => {
    resetUsedOTPs();
    setUsedOtps([]);
  };

  const uploadToImgBB = async (file) => {
    try {
      console.log('Starting ImgBB upload for file:', file.name);
      
      const formData = new FormData();
      formData.append('image', file);

      console.log('Sending request to ImgBB API...');
      
      const response = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('ImgBB API response:', data);

      if (data.success) {
        console.log('Image uploaded successfully. URL:', data.data.url);
        return data.data.url;
      } else {
        console.error('ImgBB API error:', data.error);
        throw new Error(data.error?.message || 'Failed to upload image to ImgBB');
      }
    } catch (error) {
      console.error('Error uploading to ImgBB:', error);
      throw error;
    }
  };

  const handleImageChange = async (event) => {
    if (!selectedUser) {
      setError('Please select a user first');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    console.log('Selected file:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    try {
      console.log('Starting image upload process...');
      
      // Upload to ImgBB
      const imageUrl = await uploadToImgBB(file);
      console.log('Received image URL from ImgBB:', imageUrl);
      
      if (!imageUrl) {
        throw new Error('No image URL received from ImgBB');
      }

      // Update Firestore with the new image URL
      const userRef = doc(db, 'users', selectedUser.uid);
      console.log('Updating Firestore with new image URL...');
      
      await updateDoc(userRef, {
        profileImage: imageUrl
      });
      console.log('Firestore update successful');

      // Update local state
      setProfileImage(imageUrl);
      setError('');
      setError('Profile image updated successfully');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Error in handleImageChange:', error);
      setError('Failed to update profile image. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pt-32">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage user registrations and accounts
            </p>
          </div>
          <div className="flex space-x-4">
            <button 
              onClick={() => setShowOtpSection(!showOtpSection)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Manage OTPs
            </button>
            <button 
              onClick={() => setIsCreatingUser(true)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              Create New User
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {/* OTP Management Section */}
        <div className="admin-section">
          <h2 onClick={() => setShowOtpSection(!showOtpSection)} 
              style={{ cursor: 'pointer', marginBottom: '1rem' }}>
            OTP Management {showOtpSection ? '▼' : '▶'}
          </h2>
          
          {showOtpSection && (
            <div style={otpStyles.section}>
              <div style={otpStyles.addSection}>
                <input
                  type="text"
                  value={newOtp}
                  onChange={(e) => setNewOtp(e.target.value)}
                  placeholder="Enter new 6-digit OTP"
                  maxLength={6}
                  style={otpStyles.input}
                />
                <button onClick={handleAddOtp} style={otpStyles.button}>
                  Add OTP
                </button>
              </div>

              <div style={otpStyles.otpLists}>
                <div style={otpStyles.otpListContainer}>
                  <h3 style={{ marginBottom: '1rem' }}>Valid OTPs ({validOtps.length})</h3>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {validOtps.map((otp) => (
                      <div key={otp} style={otpStyles.otpItem}>
                        <span>{otp}</span>
                        <button 
                          onClick={() => handleRemoveOtp(otp)} 
                          style={otpStyles.removeButton}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={otpStyles.otpListContainer}>
                  <h3 style={{ marginBottom: '1rem' }}>
                    Used OTPs ({usedOtps.length})
                    <button 
                      onClick={handleResetUsedOtps}
                      style={{
                        ...otpStyles.button,
                        marginLeft: '1rem',
                        fontSize: '0.875rem',
                        padding: '0.25rem 0.5rem'
                      }}
                    >
                      Clear Used OTPs
                    </button>
                  </h3>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {usedOtps.map((otp) => (
                      <div key={otp} style={otpStyles.otpItem}>
                        <span>{otp}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setFilter('all')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm
                  ${filter === 'all'
                    ? 'border-[#123524] text-[#123524]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                All Users
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm
                  ${filter === 'pending'
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Pending Review
              </button>
              <button
                onClick={() => setFilter('approved')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm
                  ${filter === 'approved'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Approved
              </button>
              <button
                onClick={() => setFilter('rejected')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm
                  ${filter === 'rejected'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Rejected
              </button>
            </nav>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Users List */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Users</h2>
            <div className="space-y-2">
              {filteredUsers.map(user => (
                <button
                  key={user.uid}
                  onClick={() => setSelectedUser(user)}
                  className={`w-full p-3 rounded-lg text-left transition-colors duration-200
                    ${selectedUser?.uid === user.uid 
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                      : 'hover:bg-gray-50'}`}
                >
                  <div className="font-medium">{user.email}</div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      {user.accounts?.length || 0} accounts
                    </span>
                    <span className={`text-sm px-2 py-1 rounded-full ${
                      user.status === 'approved' ? 'bg-green-100 text-green-800' :
                      user.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      user.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {user.status || 'unknown'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* User Details */}
          <div className="lg:col-span-3">
            {selectedUser ? (
              <div className="space-y-6">
                {/* User Info */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100">
                          {profileImage ? (
                            <img
                              src={profileImage}
                              alt="Profile"
                              className="w-full h-full object-cover"
                              onError={() => setProfileImage('')}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <label
                          htmlFor="admin-profile-image"
                          className="absolute bottom-0 right-0 bg-white rounded-full p-1.5 shadow-lg cursor-pointer
                            hover:bg-gray-50 transition-colors duration-200"
                        >
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <input
                            id="admin-profile-image"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                      <div>
                        <h2 className="text-xl font-medium text-gray-900">
                          User Details: {selectedUser.email}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          Created: {formatDate(selectedUser.createdAt)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setShowVerificationModal(true);
                        }}
                        className="px-4 py-2 bg-[#123524] text-white rounded-lg hover:bg-[#0d2318]"
                      >
                        Verify User
                      </button>
                    </div>
                  </div>
                  
                  {/* Accounts */}
                  <div className="space-y-4">
                    {selectedUser.accounts?.map(account => (
                      <div key={account.id} 
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        {editingAccount?.id === account.id ? (
                          <form onSubmit={(e) => {
                            e.preventDefault();
                            if (!validateAccountNumber(editingAccount.accountNumber)) {
                              setError('Account number must be 10 digits and not start with 0');
                              return;
                            }
                            handleAccountUpdate(account.id, editingAccount);
                          }}>
                            <div className="grid grid-cols-1 gap-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Available Balance
                                  </label>
                                  <input
                                    type="number"
                                    value={editingAccount.available}
                                    onChange={(e) => setEditingAccount({
                                      ...editingAccount,
                                      available: parseFloat(e.target.value)
                                    })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Current Balance
                                  </label>
                                  <input
                                    type="number"
                                    value={editingAccount.balance}
                                    onChange={(e) => setEditingAccount({
                                      ...editingAccount,
                                      balance: parseFloat(e.target.value)
                                    })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Account Number
                                  </label>
                                  <input
                                    type="text"
                                    value={formatAccountNumber(editingAccount.accountNumber)}
                                    onChange={(e) => {
                                      const cleaned = e.target.value.replace(/\D/g, '').slice(0, 10);
                                      setEditingAccount({
                                        ...editingAccount,
                                        accountNumber: cleaned
                                      });
                                    }}
                                    className="w-full p-2 border border-gray-300 rounded-lg font-mono"
                                    placeholder="123 456 7890"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    Must be 10 digits and not start with 0
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Routing Number
                                  </label>
                                  <input
                                    type="text"
                                    value={editingAccount.routingNumber}
                                    onChange={(e) => {
                                      const cleaned = e.target.value.replace(/\D/g, '').slice(0, 9);
                                      setEditingAccount({
                                        ...editingAccount,
                                        routingNumber: cleaned
                                      });
                                    }}
                                    className="w-full p-2 border border-gray-300 rounded-lg font-mono"
                                    placeholder="021000021"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="flex justify-end space-x-2 mt-4">
                              <button
                                type="button"
                                onClick={() => setEditingAccount(null)}
                                className="px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-lg"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                              >
                                Save Changes
                              </button>
                            </div>
                          </form>
                        ) : (
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="font-medium text-gray-900">{account.type}</h3>
                              <button
                                onClick={() => handleEditAccount(account)}
                                className="text-emerald-600 hover:text-emerald-700"
                              >
                                Edit
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mb-2">
                              <div>
                                <div className="text-sm text-gray-500">Available Balance</div>
                                <div className="font-medium">${(account.available || 0).toFixed(2)}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Current Balance</div>
                                <div className="font-medium">${(account.balance || 0).toFixed(2)}</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <div className="text-sm text-gray-500">Account Number</div>
                                <div className="font-mono font-medium">{formatAccountNumber(account.accountNumber)}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Routing Number</div>
                                <div className="font-mono font-medium">{account.routingNumber}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Add Transactions */}
                <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-medium text-gray-900">Add Transactions</h3>
                    <div className="flex space-x-4">
                      <button
                        onClick={downloadTemplate}
                        className="px-4 py-2 text-emerald-600 border border-emerald-600 rounded-lg hover:bg-emerald-50"
                      >
                        Download Template
                      </button>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="csv-upload"
                        />
                        <label
                          htmlFor="csv-upload"
                          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer"
                        >
                          Upload CSV
                        </label>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleBulkTransactionSubmit} className="space-y-4">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {newTransactions.map((transaction, index) => (
                            <tr key={index}>
                              <td className="px-2 py-2">
                        <select
                                  value={transaction.accountType}
                                  onChange={(e) => handleTransactionChange(index, 'accountType', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        >
                                  <option value="">Select</option>
                          {selectedUser.accounts.map(account => (
                            <option key={account.id} value={account.type}>{account.type}</option>
                          ))}
                        </select>
                              </td>
                              <td className="px-2 py-2">
                        <select
                                  value={transaction.type}
                                  onChange={(e) => handleTransactionChange(index, 'type', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        >
                          <option value="debit">Debit</option>
                          <option value="credit">Credit</option>
                        </select>
                              </td>
                              <td className="px-2 py-2">
                        <input
                          type="number"
                          step="0.01"
                                  value={transaction.amount}
                                  onChange={(e) => handleTransactionChange(index, 'amount', e.target.value)}
                                  className="w-24 p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  value={transaction.description}
                                  onChange={(e) => handleTransactionChange(index, 'description', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                                  required
                                />
                              </td>
                              <td className="px-2 py-2">
                        <select
                                  value={transaction.category}
                                  onChange={(e) => handleTransactionChange(index, 'category', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        >
                                  <option value="">Select</option>
                          <option value="Transfer">Transfer</option>
                          <option value="Bill Pay">Bill Pay</option>
                          <option value="Deposit">Deposit</option>
                          <option value="Withdrawal">Withdrawal</option>
                          <option value="Income">Income</option>
                          <option value="Shopping">Shopping</option>
                          <option value="Food">Food</option>
                          <option value="Transportation">Transportation</option>
                          <option value="Entertainment">Entertainment</option>
                          <option value="Other">Other</option>
                        </select>
                              </td>
                              <td className="px-2 py-2">
                          <input
                            type="date"
                                  value={transaction.date}
                                  onChange={(e) => handleTransactionChange(index, 'date', e.target.value)}
                                  className="w-32 p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            required
                          />
                              </td>
                              <td className="px-2 py-2">
                          <input
                            type="time"
                                  value={transaction.time}
                                  onChange={(e) => handleTransactionChange(index, 'time', e.target.value)}
                                  className="w-24 p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            required
                          />
                              </td>
                              <td className="px-2 py-2">
                        <select
                                  value={transaction.status}
                                  onChange={(e) => handleTransactionChange(index, 'status', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        >
                          <option value="Completed">Completed</option>
                          <option value="Pending">Pending</option>
                          <option value="Failed">Failed</option>
                        </select>
                              </td>
                              <td className="px-2 py-2">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTransactionRow(index)}
                                  className="text-red-600 hover:text-red-800"
                                  disabled={newTransactions.length === 1}
                                >
                                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex justify-between items-center pt-4">
                      <button
                        type="button"
                        onClick={handleAddTransactionRow}
                        className="px-4 py-2 text-emerald-600 hover:text-emerald-700 font-medium flex items-center"
                      >
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Row
                      </button>

                      <button
                        type="submit"
                        className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium flex items-center"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                          </>
                        ) : (
                          'Submit All Transactions'
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                {/* User Transactions */}
                <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">User Transactions</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {userTransactions.map((transaction) => (
                          <tr key={transaction.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {formatDate(transaction.date)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {transaction.accountType}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {transaction.type}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium">
                              <span className={transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}>
                                {transaction.type === 'credit' ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {transaction.description}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                transaction.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                transaction.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {transaction.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={() => handleDeleteTransaction(transaction.id)}
                                className="text-red-600 hover:text-red-800 font-medium"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {deleteStatus && (
                    <p className={`mt-4 ${deleteStatus.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>
                      {deleteStatus}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-6 text-center text-gray-500">
                Select a user to view and edit their details
              </div>
            )}
          </div>
        </div>

        {/* Verification Modal */}
        {showVerificationModal && selectedUser && (
          <UserVerificationModal
            user={selectedUser}
            onClose={() => {
              setShowVerificationModal(false);
            }}
            onApprove={(userId) => handleUserStatusUpdate(userId, 'approved')}
            onReject={handleRejectRegistration}
            onRequestInfo={handleRequestInfo}
          />
        )}

        {/* Create User Modal */}
        {isCreatingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl flex flex-col max-h-[90vh] relative">
              {/* Modal Header - Fixed */}
              <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white rounded-t-xl z-10">
                <h2 className="text-2xl font-bold text-gray-900">Create New User</h2>
                <button
                  onClick={() => setIsCreatingUser(false)}
                  className="text-gray-400 hover:text-gray-500 p-2"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6">
                <form id="createUserForm" onSubmit={handleCreateUser} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={newUserData.name}
                        onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        required
                      />
                    </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={newUserData.phone}
                        onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <select
                        value={newUserData.role}
                        onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        required
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                </div>
                
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                        <input
                          type="text"
                          value={newUserData.address.street}
                          onChange={(e) => setNewUserData({
                            ...newUserData,
                            address: {...newUserData.address, street: e.target.value}
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          value={newUserData.address.city}
                          onChange={(e) => setNewUserData({
                            ...newUserData,
                            address: {...newUserData.address, city: e.target.value}
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input
                          type="text"
                          value={newUserData.address.state}
                          onChange={(e) => setNewUserData({
                            ...newUserData,
                            address: {...newUserData.address, state: e.target.value}
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                        <input
                          type="text"
                          value={newUserData.address.zipCode}
                          onChange={(e) => setNewUserData({
                            ...newUserData,
                            address: {...newUserData.address, zipCode: e.target.value}
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Joint Account Information</h3>
                    <div className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        id="isJointAccount"
                        checked={newUserData.isJointAccount}
                        onChange={(e) => setNewUserData({...newUserData, isJointAccount: e.target.checked})}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isJointAccount" className="ml-2 block text-sm text-gray-900">
                        This is a joint account
                      </label>
                    </div>
                    
                    {newUserData.isJointAccount && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Joint Account Holder Name
                          </label>
                          <input
                            type="text"
                            value={newUserData.jointAccountHolder.name}
                            onChange={(e) => setNewUserData({
                              ...newUserData,
                              jointAccountHolder: {...newUserData.jointAccountHolder, name: e.target.value}
                            })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            required={newUserData.isJointAccount}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Joint Account Holder Email
                          </label>
                          <input
                            type="email"
                            value={newUserData.jointAccountHolder.email}
                            onChange={(e) => setNewUserData({
                              ...newUserData,
                              jointAccountHolder: {...newUserData.jointAccountHolder, email: e.target.value}
                            })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            required={newUserData.isJointAccount}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Joint Account Holder Phone
                          </label>
                          <input
                            type="tel"
                            value={newUserData.jointAccountHolder.phone}
                            onChange={(e) => setNewUserData({
                              ...newUserData,
                              jointAccountHolder: {...newUserData.jointAccountHolder, phone: e.target.value}
                            })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            required={newUserData.isJointAccount}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Initial Account Balances</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {newUserData.accounts.map((account, index) => (
                        <div key={account.type} className="space-y-4">
                          <h4 className="font-medium text-gray-700">{account.type} Account</h4>
                      <div>
                            <label className="block text-sm text-gray-600 mb-1">Available Balance</label>
                        <input
                          type="number"
                          value={account.available}
                          onChange={(e) => {
                            const newAccounts = [...newUserData.accounts];
                            newAccounts[index] = {
                              ...account,
                              available: parseFloat(e.target.value)
                            };
                            setNewUserData({...newUserData, accounts: newAccounts});
                          }}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div>
                            <label className="block text-sm text-gray-600 mb-1">Current Balance</label>
                        <input
                          type="number"
                          value={account.balance}
                          onChange={(e) => {
                            const newAccounts = [...newUserData.accounts];
                            newAccounts[index] = {
                              ...account,
                              balance: parseFloat(e.target.value)
                            };
                            setNewUserData({...newUserData, accounts: newAccounts});
                          }}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          step="0.01"
                          min="0"
                        />
                    </div>
                  </div>
                ))}
                    </div>
                  </div>
                </form>
              </div>

              {/* Modal Footer - Fixed */}
              <div className="p-6 border-t border-gray-200 flex justify-end space-x-4 sticky bottom-0 bg-white rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => setIsCreatingUser(false)}
                  className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                  form="createUserForm"
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create User'}
                  </button>
                </div>
            </div>
          </div>
        )}

        <div className="mt-8 p-4 bg-white rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Delete User Transactions</h2>
          <div className="flex gap-4 items-center">
            <input
              type="email"
              value={emailToDelete}
              onChange={(e) => setEmailToDelete(e.target.value)}
              placeholder="Enter user email"
              className="flex-1 p-2 border rounded"
            />
            <button
              onClick={handleDeleteUserTransactions}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              Delete Transactions
            </button>
          </div>
          {deleteStatus && (
            <p className={`mt-2 ${deleteStatus.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>
              {deleteStatus}
            </p>
          )}
        </div>

        <div className="mt-8 p-4 bg-white rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Reset Transfer Counts</h2>
          <div className="flex gap-4 items-center">
            <button
              onClick={() => navigate('/admin/reset-transfers')}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors duration-200"
            >
              Reset Transfer Counts
            </button>
          </div>
        </div>
      </main>
    </div>
  );
} 